import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Users, TrendingUp } from "lucide-react";
import { Reveal, Counter } from "../components/common";
import FinanceCalculator from "../components/FinanceCalculator";
import { SOLUTIONS, INDUSTRIES, STATS, STEPS } from "../data/content";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { FaqSection } from "../components/FaqSection";
import { HOME_FAQS, faqJsonLd } from "../data/faqs";

const HERO_BG = "https://images.unsplash.com/photo-1513026705753-bc3fffca8bf4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Home() {
  const navigate = useNavigate();
  const [funded, setFunded] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/recently-funded").then((r) => setFunded(r.data)).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo path="/" description="UK commercial finance broker with access to 165+ lenders. Asset finance, commercial mortgages, bridging, development and invoice finance arranged nationwide. The right funding, the right lender, the right solution." jsonLd={faqJsonLd(HOME_FAQS)} />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1A1A1A]/85" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A] via-[#1A1A1A]/70 to-transparent" />
        </div>
        {/* animated finance lines */}
        <svg className="absolute inset-0 w-full h-full opacity-30 pointer-events-none" preserveAspectRatio="none">
          {[20, 38, 56, 74, 88].map((y, i) => (
            <line key={i} x1="0" y1={`${y}%`} x2="100%" y2={`${y - 6}%`} stroke="#D8FF00" strokeWidth="1" className="finance-line" style={{ animationDelay: `${i * 1.5}s` }} />
          ))}
        </svg>

        <div className="container-x mx-auto max-w-[1400px] relative z-10 pt-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-lime/40 px-4 py-2 mb-8">
              <span className="w-2 h-2 bg-lime animate-pulse" />
              <span className="text-xs uppercase tracking-[0.25em] text-lime">UK Commercial Finance Specialists</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Business Funding<br /><span className="text-lime">Made Simple.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 mt-8 max-w-xl leading-relaxed">
              Helping UK businesses secure the right funding with access to <span className="text-white font-semibold">165+ lenders</span>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button onClick={() => navigate("/contact?src=home-hero")} data-testid="hero-get-funding-btn" className="group bg-lime text-[#1A1A1A] font-bold px-8 py-4 flex items-center justify-center gap-2 hover:glow-lime-strong transition-shadow">
                Get Funding <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => navigate("/booking")} data-testid="hero-expert-btn" className="border border-white/30 text-white font-semibold px-8 py-4 hover:border-lime hover:text-lime transition-colors">
                Speak To An Expert
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div data-testid="trust-bar" className="bg-black border-y border-[#808080]/20 overflow-hidden py-5">
        <div className="marquee-track">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center shrink-0">
              {["Access to 165+ UK Lenders", "Commercial Finance Specialists", "Fast Decisions", "Nationwide Coverage", "Professional Advice"].map((t) => (
                <span key={t} className="flex items-center text-sm uppercase tracking-widest text-grey px-10">
                  <span className="w-1.5 h-1.5 bg-lime mr-4" />{t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* FUNDING SOLUTIONS */}
      <section data-testid="funding-solutions" className="py-24 md:py-32">
        <div className="container-x mx-auto max-w-[1400px]">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-lime mb-4">Funding Solutions</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Finance for every stage of business</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-px bg-[#808080]/20 border border-[#808080]/20">
            {SOLUTIONS.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 5) * 0.06}>
                <Link to={`/solutions/${s.slug}`} data-testid={`solution-card-${s.slug}`} className="group block bg-[#1A1A1A] p-8 h-full hover:bg-[#111] transition-colors relative">
                  <div className="flex items-start justify-between">
                    <s.icon className="w-10 h-10 text-lime group-hover:-translate-y-1 transition-transform" strokeWidth={1.5} />
                    <ArrowUpRight className="w-5 h-5 text-grey group-hover:text-lime transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold mt-6 group-hover:text-lime transition-colors">{s.title}</h3>
                  <p className="text-grey text-sm mt-3 leading-relaxed">{s.blurb}</p>
                  <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-lime group-hover:w-full transition-all duration-300" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY DNA - split with stats */}
      <section data-testid="why-dna" className="py-24 md:py-32 bg-black">
        <div className="container-x mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-lime mb-4">Why DNA Finance</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">A whole-of-market broker that works for you, not the lender</h2>
            <p className="text-grey text-lg leading-relaxed mt-6">We cut through the complexity of commercial finance. With access to the entire market and dedicated relationship management, we find the right funding, quickly, and explain it in plain English.</p>
            <p className="text-grey text-lg leading-relaxed mt-4">No buzzwords. No fluff. Just trusted advice from specialists who understand UK business.</p>
            <button onClick={() => navigate("/about")} data-testid="why-about-btn" className="mt-8 inline-flex items-center gap-2 text-lime font-semibold hover:gap-3 transition-all">Learn about us <ArrowRight className="w-5 h-5" /></button>
          </Reveal>
          <div className="grid grid-cols-2 gap-px bg-[#808080]/20 border border-[#808080]/20">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.1}>
                <div className="bg-black p-6 md:p-8 h-full">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-lime tracking-tight leading-none">
                    {s.display ? s.display : <Counter to={s.value} suffix={s.suffix} />}
                  </div>
                  <p className="text-white font-semibold mt-3">{s.label}</p>
                  <p className="text-grey text-sm mt-1">{s.sub}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works" className="py-24 md:py-32">
        <div className="container-x mx-auto max-w-[1400px]">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-lime mb-4">How It Works</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Funding in four simple steps</h2>
          </Reveal>
          <div className="grid md:grid-cols-4 gap-px bg-[#808080]/20 border border-[#808080]/20">
            {STEPS.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.1}>
                <div className="bg-[#1A1A1A] p-8 h-full relative group hover:bg-[#111] transition-colors">
                  <span className="text-6xl font-black text-[#808080]/20 group-hover:text-lime/30 transition-colors">{step.n}</span>
                  <h3 className="text-lg font-bold mt-4">{step.title}</h3>
                  <p className="text-grey text-sm mt-3 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section data-testid="industries" className="py-24 md:py-32 bg-black">
        <div className="container-x mx-auto max-w-[1400px]">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-lime mb-4">Industries We Fund</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Sector specialists across the UK</h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={(i % 3) * 0.08}>
                <div data-testid={`industry-${ind.name.toLowerCase().replace(/\s/g, "-")}`} className="group relative h-48 md:h-56 overflow-hidden border border-[#808080]/20">
                  <img src={ind.image} alt={ind.name} className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-black/65 group-hover:bg-black/50 transition-colors" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <ind.icon className="w-7 h-7 text-lime mb-2" strokeWidth={1.5} />
                    <h3 className="text-lg font-bold">{ind.name}</h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <FinanceCalculator />

      {/* RECENTLY FUNDED */}
      <section data-testid="recently-funded" className="py-24 md:py-32 bg-black">
        <div className="container-x mx-auto max-w-[1400px]">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-lime mb-4">Recently Funded</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Deals completed by DNA Finance</h2>
          </Reveal>
          <div className="border border-[#808080]/20 divide-y divide-[#808080]/20">
            {funded.map((d, i) => (
              <Reveal key={`${d.company}-${d.location}`} delay={i * 0.04}>
                <div className="flex items-center justify-between p-6 hover:bg-[#111] transition-colors group">
                  <div className="flex items-center gap-6">
                    <span className="text-2xl md:text-3xl font-black text-lime tracking-tight min-w-[120px]">{d.amount}</span>
                    <div>
                      <p className="font-bold">{d.company}</p>
                      <p className="text-grey text-sm">{d.type}</p>
                    </div>
                  </div>
                  <span className="text-grey text-sm uppercase tracking-wide hidden sm:flex items-center gap-2"><MapPin className="w-4 h-4 text-lime" />{d.location}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LENDER COMPARISON placeholder */}
      <section className="py-24 md:py-32">
        <div className="container-x mx-auto max-w-[1400px]">
          <Reveal>
            <div data-testid="lender-dashboard-placeholder" className="border border-[#808080]/30 p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6"><Users className="w-7 h-7 text-lime" /><span className="text-xs uppercase tracking-[0.3em] text-grey">Lender Panel</span></div>
              <h3 className="text-2xl font-bold mb-4">Lender comparison dashboard</h3>
              <p className="text-grey leading-relaxed mb-6 max-w-2xl">Compare indicative rates and criteria across our 165+ lender panel. Our advisers run the full comparison for you.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {["High-street banks", "Challenger & specialist lenders", "Asset & invoice financiers", "Bridging & development funds"].map((l) => (
                  <div key={l} className="flex items-center justify-between bg-[#111] border border-[#808080]/20 px-4 py-3 text-sm">
                    <span>{l}</span><span className="text-lime font-semibold">Available</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* KNOWLEDGE HUB */}
      <section data-testid="knowledge-preview" className="py-24 md:py-32">
        <div className="container-x mx-auto max-w-[1400px]">
          <Reveal className="flex items-end justify-between mb-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-lime mb-4">Knowledge Hub</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Commercial finance insight</h2>
            </div>
            <Link to="/knowledge-hub" className="hidden sm:inline-flex items-center gap-2 text-lime font-semibold hover:gap-3 transition-all">View all <ArrowRight className="w-5 h-5" /></Link>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <Link to={`/knowledge-hub/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block border border-[#808080]/20 hover:border-lime/40 transition-colors h-full">
                  <div className="h-48 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs uppercase tracking-widest text-lime">{p.category}</span>
                    <h3 className="text-lg font-bold mt-3 group-hover:text-lime transition-colors leading-snug">{p.title}</h3>
                    <p className="text-grey text-sm mt-3 line-clamp-2">{p.excerpt}</p>
                    <p className="text-xs text-grey mt-4">{p.read_time}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FaqSection faqs={HOME_FAQS} intro="Straight answers on how commercial finance brokerage works, what it costs and what to expect." />

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-black border-t border-[#808080]/20">
        <div className="container-x mx-auto max-w-[1400px] text-center">
          <Reveal>
            <TrendingUp className="w-12 h-12 text-lime mx-auto mb-8" strokeWidth={1.5} />
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter max-w-3xl mx-auto">Ready to fund your next move?</h2>
            <p className="text-grey text-lg mt-6 max-w-xl mx-auto">Speak to a commercial finance specialist today. No obligation, no jargon.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <button onClick={() => navigate("/contact?src=home-final-cta")} data-testid="final-get-funding-btn" className="bg-lime text-[#1A1A1A] font-bold px-10 py-4 hover:glow-lime-strong transition-shadow">Get Funding</button>
              <button onClick={() => navigate("/booking")} data-testid="final-book-btn" className="border border-white/30 font-semibold px-10 py-4 hover:border-lime hover:text-lime transition-colors">Book a Consultation</button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
